import React from 'react';
import { getSalesSummary } from '../../services/api';
import { ReportPage, formatMoney } from './ReportPage';
import { useReport } from './useReport';

const formatDay = (day) => new Date(`${day}T00:00:00Z`).toLocaleDateString(undefined, {
  weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
});

const Change = ({ pct }) => {
  if (pct == null) return <span className="report-change">No previous data</span>;
  const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
  const arrow = { up: '▲', down: '▼', flat: '–' }[dir];
  return <span className={`report-change is-${dir}`}>{arrow} {Math.abs(pct)}% vs previous period</span>;
};

const Kpi = ({ label, metric, format }) => (
  <div className="report-kpi">
    <span className="report-kpi-label">{label}</span>
    <span className="report-kpi-value">{format(metric.current)}</span>
    <Change pct={metric.change_pct} />
    <span className="report-kpi-prev">Previous: {format(metric.previous)}</span>
  </div>
);

const Summary = ({ summary, currencyKey, paymentMethods }) => {
  const money = (value) => formatMoney(value, currencyKey);
  const method = paymentMethods.find((m) => m.value === summary.top_payment_method);
  const maxSales = Math.max(...summary.daily.map((d) => d.net_sales), 1);

  return (
    <section className="report-section">
      {currencyKey && <h2 className="report-currency">{currencyKey}</h2>}

      <div className="report-kpis">
        <Kpi label="Net sales" metric={summary.net_sales} format={money} />
        <Kpi label="Gross sales" metric={summary.gross_sales} format={money} />
        <Kpi label="Orders" metric={summary.orders} format={String} />
        <Kpi label="Average ticket" metric={summary.average_ticket} format={money} />
      </div>

      <ul className="report-insights">
        {summary.best_day && <li>Best day: <strong>{formatDay(summary.best_day.day)}</strong></li>}
        {summary.weakest_day && <li>Weakest day: <strong>{formatDay(summary.weakest_day.day)}</strong></li>}
        {method && <li>Top payment method: <strong className="report-method">{method.label}</strong></li>}
      </ul>

      <div className="report-chart" role="img" aria-label="Net sales per day">
        {summary.daily.map((d) => (
          <div
            key={d.day}
            className="report-bar"
            style={{ height: `${(d.net_sales / maxSales) * 100}%` }}
            title={`${formatDay(d.day)}: ${money(d.net_sales)} (${d.orders} orders)`}
          />
        ))}
      </div>
    </section>
  );
};

const SalesReport = ({ restaurants, userId, authenticatedFetch, currencies, paymentMethods }) => {
  const report = useReport(getSalesSummary, { restaurants, userId, authenticatedFetch });

  return (
    <ReportPage report={report}>
      {(summaries) => summaries.map((summary) => (
        <Summary
          key={summary.currency}
          summary={summary}
          currencyKey={currencies.find((c) => c.value === summary.currency)?.key}
          paymentMethods={paymentMethods}
        />
      ))}
    </ReportPage>
  );
};

export default SalesReport;
